import React, { useEffect, useState } from 'react';
import { Add } from '@mui/icons-material';
import { Tab, Tabs } from '@mui/material';
import {
    ZinantisRoomServiceState,
    ZinantisServiceState,
    VisibleContentScope,
} from '../../../types/zitoneoServicesTypes';
import useTranslate from '../../../../../customHooks/useTranslate';
import { createNewHotelService } from '../../../utils/zitoneoServicesUtils';
import Button from '../../../../../muiComponents/Button';
import { useZitoneoServices } from '../../../contexts/ZitoneoContentContext';
import ZitoneoServiceInstance from './ZitoneoServiceInstance';
import ServiceInstanceTab from './ServiceInstanceTab';
import { useZitoneoUI } from '../../../contexts/ZitoneoUIContext';
import ServiceItemHeader from './ServiceItemHeader';
import useServiceAttributeQuality from './useServiceAttributeQuality';
import ServiceAttributes from './ServiceAttributes';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';

interface ZitoneoServiceItemProps {
    id: string;
    name: string;
    hotelServices: (ZinantisServiceState | ZinantisRoomServiceState)[];
    isVisibleServicesDetail: boolean;
    onVisibleServiceDetail: (idService: number) => void;
    isNewService: boolean;
    roomId?: string;
}

const ZitoneoServiceItem = ({
    id,
    name,
    hotelServices,
    isVisibleServicesDetail,
    isNewService,
    roomId,
    onVisibleServiceDetail,
}: ZitoneoServiceItemProps) => {
    const translate = useTranslate();
    const {
        handleAddExceptionToService,
        handleAddService,
        handleDeleteService,
        handleChangeRoomServiceAttributeValue,
        handleDeleteRoomServiceAttributeValue,
        handleDeleteRoomService,
        handleEditRoomServiceDescription,
        selectedCategory,
        isLoading,
    } = useZitoneoServices();
    const {
        focusedHotelServiceId,
        handleFocusHotelService,
        visibleContentScope,
    } = useZitoneoUI();
    const isRoomService = visibleContentScope === VisibleContentScope.ROOM;

    const [activeTab, setActiveTab] = useState(0);

    const {
        attributesAvailable,
        attributesFilled,
        contentQualityLevel,
        attributesAreLoading,
    } = useServiceAttributeQuality(hotelServices);

    const hasExceptions = hotelServices.some(
        hotelService =>
            'Exceptions' in hotelService && hotelService.Exceptions.length > 0
    );

    useEffect(() => {
        if (!focusedHotelServiceId || !isVisibleServicesDetail) {
            return;
        }

        const focusedIndex = hotelServices.findIndex(
            hotelService => hotelService.Id === focusedHotelServiceId
        );

        if (focusedIndex >= 0) {
            setActiveTab(focusedIndex);
        }
    }, [focusedHotelServiceId, hotelServices, isVisibleServicesDetail]);

    if (isRoomService) {
        const roomService = hotelServices[0] as ZinantisRoomServiceState;

        return (
            <div className='zitoneo-service__item'>
                <ServiceItemHeader
                    name={name}
                    isNewService={false}
                    instanceCount={1}
                    attributesAreLoading={attributesAreLoading}
                    attributesFilled={attributesFilled}
                    attributesAvailable={attributesAvailable}
                    contentQualityLevel={contentQualityLevel}
                    isExpanded={isVisibleServicesDetail}
                    onToggle={() => onVisibleServiceDetail(parseInt(id))}
                    onDelete={
                        roomId
                            ? () =>
                                  handleDeleteRoomService(
                                      roomId,
                                      roomService.Id
                                  )
                            : undefined
                    }
                />
                {isVisibleServicesDetail && !attributesAreLoading && (
                    <div className='flex-column gap-30 zitoneo-service__attributes__container'>
                        <div className='flex-column gap-30'>
                            <TextInput
                                labelText='description2'
                                deleteIcon={false}
                                value={roomService.Description || ''}
                                multiline
                                rows={3}
                                handleChange={(
                                    _event: unknown,
                                    value: string
                                ) => {
                                    if (roomId) {
                                        handleEditRoomServiceDescription(
                                            roomId,
                                            roomService.Id,
                                            value
                                        );
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'var(--background)',
                                    paddingBlock: '16px',
                                }}
                                labelProps={{
                                    sx: {
                                        backgroundColor:
                                            'var(--background) !important',
                                        padding: '0 4px',
                                    },
                                }}
                            />
                            <ServiceAttributes
                                serviceId={roomService.Id}
                                attributeDefinitions={
                                    roomService.AttributeDefinitions
                                }
                                attributeValues={roomService.AttributeValues}
                                disabled={false}
                                onChange={(
                                    idValue,
                                    value,
                                    idService,
                                    idAttributeDefinition
                                ) => {
                                    if (roomId) {
                                        handleChangeRoomServiceAttributeValue(
                                            roomId,
                                            idService,
                                            idAttributeDefinition,
                                            idValue,
                                            value
                                        );
                                    }
                                }}
                                onDelete={(idValue, idService) => {
                                    if (roomId) {
                                        handleDeleteRoomServiceAttributeValue(
                                            roomId,
                                            idService,
                                            idValue
                                        );
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='zitoneo-service__item'>
            <ServiceItemHeader
                name={name}
                isNewService={isNewService}
                instanceCount={hotelServices.length}
                attributesAreLoading={attributesAreLoading}
                attributesFilled={attributesFilled}
                attributesAvailable={attributesAvailable}
                contentQualityLevel={contentQualityLevel}
                hasExceptions={hasExceptions}
                isExpanded={isVisibleServicesDetail}
                onToggle={() => onVisibleServiceDetail(parseInt(id))}
            />
            {isVisibleServicesDetail && !attributesAreLoading && (
                <div>
                    <div>
                        <div className='flex flex-align-center flex-between gap-20 border-color-bottom'>
                            <Tabs
                                value={activeTab}
                                variant='scrollable'
                                scrollButtons='auto'
                                onChange={(_, newValue) => {
                                    if (newValue === hotelServices.length) {
                                        if (selectedCategory) {
                                            const newService =
                                                createNewHotelService(
                                                    {
                                                        Id: id,
                                                        Name: name,
                                                        IdServiceCategory:
                                                            parseInt(
                                                                selectedCategory.Id
                                                            ),
                                                        IsNewService:
                                                            isNewService,
                                                        ForRoom: false,
                                                        ForHotel: true,
                                                    },
                                                    selectedCategory
                                                );

                                            handleFocusHotelService(
                                                newService.Id
                                            );
                                            handleAddService(newService);
                                            setActiveTab(hotelServices.length);
                                        }
                                    } else {
                                        setActiveTab(newValue);
                                    }
                                }}
                            >
                                {(hotelServices as ZinantisServiceState[]).map(
                                    (hotelService, index) => (
                                        <ServiceInstanceTab
                                            key={hotelService.Id}
                                            id={hotelService.Id}
                                            name={name}
                                            serviceName={
                                                hotelService.Name || ''
                                            }
                                            index={index}
                                            isActive={activeTab === index}
                                            isDisabled={isLoading}
                                        />
                                    )
                                )}
                                <Tab icon={<Add />} />
                            </Tabs>
                            <div className='flex gap-10'>
                                <Button
                                    startIcon='ErrorOutlineOutlined'
                                    onClick={() => {
                                        const currentService =
                                            hotelServices[activeTab];
                                        if (currentService) {
                                            handleAddExceptionToService(
                                                currentService.Id
                                            );
                                        }
                                    }}
                                    variant='text'
                                    color='secondary'
                                >
                                    {translate('add_errata')}
                                </Button>
                                <Button
                                    startIcon='DeleteOutlined'
                                    onClick={() => {
                                        const serviceToDelete =
                                            hotelServices[activeTab];
                                        if (serviceToDelete) {
                                            const newActiveTab =
                                                activeTab >=
                                                hotelServices.length - 1
                                                    ? Math.max(0, activeTab - 1)
                                                    : activeTab;
                                            setActiveTab(newActiveTab);
                                            handleDeleteService(
                                                serviceToDelete.Id
                                            );
                                        }
                                    }}
                                    sx={{
                                        color: 'var(--error-400)',
                                        whiteSpace: 'nowrap',
                                        marginRight: '12px',
                                    }}
                                    variant='text'
                                >
                                    {translate('delete')} {`#${activeTab + 1}`}
                                </Button>
                            </div>
                        </div>
                        <div className='zitoneo-service__attributes__container'>
                            <ZitoneoServiceInstance
                                key={hotelServices[activeTab]?.Id}
                                hotelService={
                                    hotelServices[
                                        activeTab
                                    ] as ZinantisServiceState
                                }
                                isDisabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZitoneoServiceItem;
